// src/Components/Chat/ChatContainer.jsx
import React, { useState, useEffect, useRef } from "react";
import ChatWindow from "./ChatWindow";
import ChatInput from "./ChatInput";
import { chatService } from "../../Services/chatServices";
import { findPeek } from "../../Services/chatPeek";

// SVG Icon Components
const ChatIcon = ({ className = "w-6 h-6" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

const ReviewIcon = ({ className = "w-6 h-6" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ResultsIcon = ({ className = "w-6 h-6" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const CloseIcon = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const ChatContainer = ({
  roomData,
  roomDocId,
  currentUserId,
  playerName,
  className = "",
  position = "bottom-right", // bottom-right, bottom-left, top-right, top-left
  // Optional: the caller's own known phase ("lobby" | "review" | "results"),
  // e.g. GameFlow.jsx's gamePhase state. chatService.getCurrentChatPhase()
  // infers phase from roomData.gameStatus alone, which only ever holds
  // "waiting" | "playing" | "ended"/"finished" in this app's data model —
  // it has no way to distinguish "reviewing this round" from "playing this
  // round" from that field, so without this override the FAB is stuck on
  // the lobby theme for the entire game past the lobby screen.
  phaseOverride,
}) => {
  const [isOpen, setIsOpen] = useState(false); // Start minimized
  const [currentPhase, setCurrentPhase] = useState("lobby");
  const [isVisible, setIsVisible] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadMessageId, setLastReadMessageId] = useState(null);
  // A short preview of the newest message from someone else, while chat is closed.
  const [peek, setPeek] = useState(null);
  const peekSeenRef = useRef(null);

  // Determine current chat phase based on game state - simplified
  useEffect(() => {
    if (phaseOverride) {
      setCurrentPhase(phaseOverride);
      setIsVisible(true);
      return;
    }
    if (roomData) {
      const phaseInfo = chatService.getCurrentPhaseInfo(roomData);
      setCurrentPhase(phaseInfo.phase);

      // Chat is always visible - no complex logic
      setIsVisible(true);

      // Auto-open chat when entering review or results phase
      // if ((phaseInfo.phase === "review" || phaseInfo.phase === "results") && !isOpen) {
      //   setIsOpen(true);
      // }
    }
  }, [phaseOverride, roomData?.gameStatus, roomData?.currentRound, roomData?.rounds, isOpen]);

  // Calculate unread messages when chat is closed - simplified
  useEffect(() => {
    if (!isOpen && roomData?.chat?.[currentPhase]) {
      const messages = chatService.getPhaseMessages(roomData, currentPhase);
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.id !== lastReadMessageId) {
          // Count unread messages since last read
          const lastReadIndex = messages.findIndex(
            (msg) => msg.id === lastReadMessageId
          );
          const unreadMessages =
            lastReadIndex >= 0 ? messages.slice(lastReadIndex + 1) : messages;
          setUnreadCount(
            unreadMessages.filter((msg) => msg.userId !== currentUserId).length
          );
        }
      }
    } else if (isOpen) {
      // Mark as read when chat is open
      const messages = chatService.getPhaseMessages(roomData, currentPhase);
      if (messages.length > 0) {
        setLastReadMessageId(messages[messages.length - 1].id);
        setUnreadCount(0);
      }
    }
  }, [roomData, currentPhase, isOpen, lastReadMessageId, currentUserId]);

  // Preview new messages from other players while the chat is closed. Chat is off
  // while people are answering, so nothing pops up mid-round.
  useEffect(() => {
    if (!roomData) return; // wait for the room, so its backlog isn't mistaken for new messages
    const result = findPeek({
      messages: chatService.getPhaseMessages(roomData, currentPhase),
      phase: currentPhase,
      seen: peekSeenRef.current,
      currentUserId,
      canPeek: !isOpen && currentPhase !== "playing",
    });
    peekSeenRef.current = result.seen;
    if (result.peek) setPeek(result.peek);
  }, [roomData, currentPhase, isOpen, currentUserId]);

  // The preview goes away by itself, and as soon as the chat is opened.
  useEffect(() => {
    if (!peek) return undefined;
    const timer = setTimeout(() => setPeek(null), 4500);
    return () => clearTimeout(timer);
  }, [peek]);
  useEffect(() => {
    if (isOpen) setPeek(null);
  }, [isOpen]);

  // Don't render if not visible in current phase
  if (!isVisible) {
    return null;
  }

  // Handle message sent callback
  const handleMessageSent = () => {
    // Reset unread count when user sends message
    setUnreadCount(0);
  };

  // Get chat status for input
  const isChatDisabled = () => {
    if (currentPhase === "playing") return true;
    return false;
  };

  // Get phase-specific styling and icons
  const getPhaseTheme = (phase) => {
    const themes = {
      lobby: {
        fabBg: "bg-blue-600 hover:bg-blue-700",
        icon: <ChatIcon className="w-6 h-6" />,
        headerBg: "bg-blue-50",
        headerText: "text-blue-800",
        borderColor: "border-blue-200",
      },
      review: {
        fabBg: "bg-yellow-600 hover:bg-yellow-700",
        icon: <ChatIcon className="w-6 h-6" />,
        headerBg: "bg-yellow-50",
        headerText: "text-yellow-800",
        borderColor: "border-yellow-200",
      },
      results: {
        fabBg: "bg-green-600 hover:bg-green-700",
        icon: <ChatIcon className="w-6 h-6" />,
        headerBg: "bg-green-50",
        headerText: "text-green-800",
        borderColor: "border-green-200",
      },
    };

    return themes[phase] || themes.lobby;
  };

  // Get position classes with mobile responsiveness
  const getPositionClasses = (pos) => {
    const positions = {
      "bottom-right": "bottom-6 right-6 md:bottom-6 md:right-6",
      "bottom-left": "bottom-6 left-6 md:bottom-6 md:left-6",
      "top-right": "top-6 right-6 md:top-6 md:right-6",
      "top-left": "top-6 left-6 md:top-6 md:left-6",
    };
    return positions[pos] || positions["bottom-right"];
  };

  const theme = getPhaseTheme(currentPhase);
  const chatDisabled = isChatDisabled();
  const positionClasses = getPositionClasses(position);

  // FAB (Floating Action Button) when minimized
  if (!isOpen) {
    return (
      <>
      {peek && (
        <div
          className="pointer-events-none fixed inset-x-0 top-0 z-[90] flex justify-center px-4 pt-[calc(env(safe-area-inset-top,0px)+7rem)]"
          aria-live="polite"
        >
          <button
            key={peek.id}
            onClick={() => {
              setPeek(null);
              setIsOpen(true);
            }}
            className="chat-peek pointer-events-auto flex max-w-sm items-center gap-2 rounded-full border border-brand-border bg-brand-surface/95 px-4 py-2.5 text-left shadow-[0_8px_24px_-8px_rgba(31,41,55,0.25)] backdrop-blur-sm"
          >
            <ChatIcon className="h-4 w-4 flex-shrink-0 text-brand-accent" />
            <span className="truncate text-sm text-brand-text">
              <span className="font-bold text-brand-accent">{peek.name}</span> {peek.text}
            </span>
          </button>
        </div>
      )}
      <div className={`fixed ${positionClasses} z-50 ${className}`}>
        <button
          onClick={() => setIsOpen(true)}
          className={`${theme.fabBg} text-white p-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-4 focus:ring-opacity-50`}
          style={{ minWidth: "64px", minHeight: "64px" }}
        >
          {/* Unread badge */}
          {unreadCount > 0 && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full min-w-6 h-6 flex items-center justify-center font-bold animate-pulse">
              {unreadCount > 99 ? "99+" : unreadCount}
            </div>
          )}

          {/* SVG Icon */}
          <div className="flex items-center justify-center">{theme.icon}</div>
        </button>

        {/* Tooltip - hidden on mobile */}
        <div className="absolute bottom-full right-0 mb-2 opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none hidden md:block">
          <div className="bg-gray-800 text-white text-sm px-3 py-1 rounded-lg whitespace-nowrap">
            {currentPhase === "lobby" && "Open lobby chat"}
            {currentPhase === "review" && "Open review discussion"}
            {currentPhase === "results" && "Open results chat"}
            {unreadCount > 0 && ` (${unreadCount} new)`}
          </div>
          <div className="absolute top-full right-6 border-4 border-transparent border-t-gray-800"></div>
        </div>
      </div>
      </>
    );
  }

  // Full chat window when open - responsive positioning with proper desktop positioning
  return (
    <>
      {/* Mobile: Full screen overlay */}
      <div
        className={`fixed z-50 md:hidden ${className}`}
        style={{
          left: "clamp(16px, 5vw, 24px)",
          right: "clamp(16px, 5vw, 24px)",
          bottom: "clamp(16px, 3vh, 24px)",
          top: "clamp(60px, 10vh, 100px)",
        }}
      >
        {/* Mobile chat window */}
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden w-full h-full flex flex-col max-h-screen">
          {/* Mobile content same as desktop but responsive */}
          {renderChatContent()}
        </div>

        {/* Mobile overlay background */}
        <div
          className="fixed inset-0 bg-black bg-opacity-20 -z-10"
          onClick={() => setIsOpen(false)}
        ></div>
      </div>

      {/* Desktop: Corner positioned */}
      <div
        className={`fixed ${positionClasses} z-50 hidden md:block ${className}`}
      >
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden w-96 h-[500px] flex flex-col">
          {renderChatContent()}
        </div>
      </div>
    </>
  );

  // Shared chat content for both mobile and desktop
  function renderChatContent() {
    return (
      <>
        {/* Chat header */}
        <div
          className={`${theme.headerBg} ${theme.borderColor} border-b px-4 py-3 flex-shrink-0`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={theme.headerText}>{theme.icon}</div>
              <div>
                <h3
                  className={`font-semibold ${theme.headerText} text-sm md:text-base`}
                >
                  {currentPhase === "lobby" && "Lobby Chat"}
                  {currentPhase === "review" && "Review Discussion"}
                  {currentPhase === "results" && "Results Chat"}
                </h3>
                <p className={`text-xs ${theme.headerText} opacity-75`}>
                  {roomData?.chat?.[currentPhase]?.length || 0} messages
                  {chatDisabled && " • Chat disabled"}
                </p>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={() => setIsOpen(false)}
              className={`${theme.headerText} hover:opacity-70 transition-opacity p-2 rounded-full hover:bg-black hover:bg-opacity-10`}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Chat content - messages and input */}
        <div className="flex flex-col flex-1 min-h-0">
          {/* Messages window */}
          <div className="flex-1 min-h-0">
            <ChatWindow
              roomData={roomData}
              currentPhase={currentPhase}
              currentUserId={currentUserId}
              className="h-full"
            />
          </div>

          {/* Input area */}
          <div className="flex-shrink-0 bg-white">
            <ChatInput
              roomDocId={roomDocId}
              currentPhase={currentPhase}
              currentUserId={currentUserId}
              playerName={playerName}
              disabled={chatDisabled}
              onMessageSent={handleMessageSent}
            />
          </div>
        </div>

        {/* Game status indicator */}
        {roomData?.gameStatus === "ended" && (
          <div className="bg-gray-100 border-t px-4 py-2 text-center flex-shrink-0">
            <span className="text-xs text-gray-600">
              Game ended • Chat remains active until{" "}
              {roomData?.expiresAt
                ? roomData.expiresAt
                    .toDate()
                    .toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                : "room expires"}
            </span>
          </div>
        )}
      </>
    );
  }
};

export default ChatContainer;
